import { Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/shadcn/table'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import type { ServiceConnectionModel } from '@/api/model'
import { getServiceConnectionDefinition } from '../registry'
import { ServiceConnectionTypeIcon } from '../components/ServiceConnectionTypeIcon'
import { ServiceConnectionRowActions } from './ServiceConnectionRowActions'

interface Props {
  isLoading: boolean
  isError: boolean
  connections: ServiceConnectionModel[]
  canEdit: boolean
  onEdit: (connection: ServiceConnectionModel) => void
  onSyncEmployees: (connection: ServiceConnectionModel) => void
}

export function ServiceConnectionsDataTable({
  isLoading,
  isError,
  connections,
  canEdit,
  onEdit,
  onSyncEmployees,
}: Props) {
  const { t } = useTranslation(['service-connections', 'common'])

  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
        <TableRow>
          <TableHead>{t('list.columns.type')}</TableHead>
          <TableHead>{t('list.columns.name')}</TableHead>
          {canEdit && (
            <TableHead className="w-28 text-right">{t('columns.actions', { ns: 'common' })}</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        <LoaderTable
          isLoading={isLoading}
          isError={isError}
          isEmpty={connections.length === 0}
          emptyMessage={t('list.empty')}
          errorMessage={t('list.loadError')}
          colSpan={canEdit ? 3 : 2}
        >
          {connections.map((connection) => {
            const definition = getServiceConnectionDefinition(connection.type)
            return (
              <TableRow key={connection.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {definition && <ServiceConnectionTypeIcon icon={definition.icon} />}
                    <span>{definition ? t(definition.labelKey) : connection.type}</span>
                  </div>
                </TableCell>
                <TableCell className="font-medium">{connection.name ?? '—'}</TableCell>
                {canEdit && (
                  <TableCell>
                    <div className="flex items-center justify-end">
                      <Button variant="ghost" size="icon" onClick={() => onEdit(connection)}>
                        <Pencil className="size-4" />
                      </Button>
                      <ServiceConnectionRowActions
                        connection={connection}
                        onSyncEmployees={onSyncEmployees}
                      />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </LoaderTable>
      </TableBody>
    </Table>
  )
}
